import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/lib/api";
import type {
  CreateUserInput,
  DeliverProUser,
  ManageUsersListResponse,
  ManagedUserResponse,
  ResetPasswordMethod,
  ResetPasswordResponse,
  UpdateUserInput,
  UserSummary,
  UsersListResponse,
} from "@/types";

/**
 * Fetches the directory of users used for reviewer selection.
 *
 * Best-effort by design: the only consumer (ReviewerCombobox) degrades to a free-text
 * field when this query fails or returns nothing, so we disable retries and suppress the
 * global error toast (`_suppressErrorToast`) — a missing/unavailable directory must never
 * block a reviewer from being recorded.
 */
export function useUsers() {
  const client = useApiClient();

  return useQuery<UserSummary[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await client.get<UsersListResponse>("/api/users", {
        _suppressErrorToast: true,
      });
      return response.data.users ?? [];
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── User Management (Leadership) ────────────────────────────────────────────
//
// Cognito-backed directory mirrored to deliverpro_users. See specs/api/users.yaml.
//   GET  /api/users/manage?enabled=true|false        → { users }
//   POST /api/users/manage                           → { user }   (Leadership)
//   PUT  /api/users/manage/{userId}                  → { user, session_revoked }   (Leadership)
//   PUT  /api/users/manage/{userId}/deactivate       → { user }   (Leadership)
//   PUT  /api/users/manage/{userId}/activate         → { user }   (Leadership)
//   POST /api/users/manage/{userId}/reset-password   → { temp_password } | { sent: true }

const MANAGE_USERS_KEY = ["users", "manage"] as const;

/**
 * List managed users. `enabled` maps to the query param:
 *   true  → active users only, false → deactivated only, undefined → all.
 */
export function useManageUsers(enabled?: boolean) {
  const client = useApiClient();

  return useQuery<DeliverProUser[]>({
    queryKey: [...MANAGE_USERS_KEY, { enabled }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (enabled !== undefined) params.append("enabled", String(enabled));
      const query = params.toString();
      const response = await client.get<ManageUsersListResponse>(
        `/api/users/manage${query ? `?${query}` : ""}`,
      );
      return response.data.users ?? [];
    },
  });
}

/** Create a user (invite). Leadership only. Invalidates the managed-user lists. */
export function useCreateUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<DeliverProUser, unknown, CreateUserInput>({
    mutationFn: async (input) => {
      const response = await client.post<ManagedUserResponse>(
        "/api/users/manage",
        input,
      );
      return response.data.user;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MANAGE_USERS_KEY });
    },
  });
}

/**
 * Result of PUT /api/users/manage/{userId}. `session_revoked` is true when the backend changed the
 * user's role and globally signed them out (they must sign in again for the new role to apply).
 * Older backends omit it — treat `undefined` as "unknown".
 */
export interface UpdateUserResult {
  user: DeliverProUser;
  session_revoked?: boolean;
  /**
   * True when the change is committed (and in force — authorization reads the DB) but mirroring it
   * to Cognito failed. Surfaced as a warning; the change itself does not need to be retried.
   */
  cognito_sync_failed?: boolean;
}

/** Update a user's name and/or role. Leadership only. */
export function useUpdateUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<
    UpdateUserResult,
    unknown,
    { userId: string; input: UpdateUserInput }
  >({
    mutationFn: async ({ userId, input }) => {
      const response = await client.put<
        ManagedUserResponse & {
          session_revoked?: boolean;
          cognito_sync_failed?: boolean;
        }
      >(`/api/users/manage/${userId}`, input, {
        // EditUserModal shows the server's message (SELF_ROLE_CHANGE_FORBIDDEN,
        // LAST_LEADERSHIP_USER, …) — suppress the generic global toast so it isn't doubled.
        _suppressErrorToast: true,
      });
      return {
        user: response.data.user,
        session_revoked: response.data.session_revoked,
        cognito_sync_failed: response.data.cognito_sync_failed,
      };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MANAGE_USERS_KEY });
    },
  });
}

/** Deactivate a user (disable Cognito account + global sign-out). Leadership only. */
export function useDeactivateUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<DeliverProUser, unknown, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const response = await client.put<ManagedUserResponse>(
        `/api/users/manage/${userId}/deactivate`,
        undefined,
        // DeactivateUserModal shows the server's message (LAST_LEADERSHIP_USER,
        // SELF_DEACTIVATION_FORBIDDEN, …) — suppress the generic global toast.
        { _suppressErrorToast: true },
      );
      return response.data.user;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MANAGE_USERS_KEY });
    },
  });
}

/** Reactivate a previously deactivated user. Leadership only. */
export function useActivateUser() {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation<DeliverProUser, unknown, { userId: string }>({
    mutationFn: async ({ userId }) => {
      const response = await client.put<ManagedUserResponse>(
        `/api/users/manage/${userId}/activate`,
      );
      return response.data.user;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MANAGE_USERS_KEY });
    },
  });
}

/**
 * Reset a user's password. Leadership only.
 *   method=temporary → returns { temp_password }
 *   method=link      → returns { sent: true } (Cognito forgot-password email)
 */
export function useResetPassword() {
  const client = useApiClient();

  return useMutation<
    ResetPasswordResponse,
    unknown,
    { userId: string; method: ResetPasswordMethod }
  >({
    mutationFn: async ({ userId, method }) => {
      const response = await client.post<ResetPasswordResponse>(
        `/api/users/manage/${userId}/reset-password`,
        { method },
      );
      return response.data;
    },
  });
}
